import { Item } from '@factorialco/deprecated-design-system'
import { DatevLaudsClientDataQuery } from 'generated/resources/graphql'

import { Company } from 'modules/core/hooks/useCurrentSession'

import { DatevMapping, DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

type IntegrationsDatevLaudsBusinessUnit = NonNullable<
  NonNullable<
    NonNullable<
      DatevLaudsClientDataQuery['integrationsDatevLauds']['clientDataJobResultsConnection']['nodes'][number]
    >['businessUnits']
  >[number]
>

type IntegrationsDatevLaudsDepartment = NonNullable<
  NonNullable<
    NonNullable<
      DatevLaudsClientDataQuery['integrationsDatevLauds']['clientDataJobResultsConnection']['nodes'][number]
    >['departments']
  >[number]
>

type IntegrationsDatevLaudsHealthInsurer = NonNullable<
  NonNullable<
    NonNullable<
      DatevLaudsClientDataQuery['integrationsDatevLauds']['clientDataJobResultsConnection']['nodes'][number]
    >['healthInsurers']
  >[number]
>

type IntegrationsDatevLaudsSalaryType = NonNullable<
  NonNullable<
    NonNullable<
      DatevLaudsClientDataQuery['integrationsDatevLauds']['clientDataJobResultsConnection']['nodes'][number]
    >['salaryTypes']
  >[number]
>

export type ClientDataProps = {
  businessUnits: Item[]
  salaryTypes: Item[]
  departments: Item[]
  loading: boolean
}

export interface ClientDataResultProps {
  salaryTypesOptions: IntegrationsDatevLaudsSalaryType[]
  businessUnitsOptions: IntegrationsDatevLaudsBusinessUnit[]
  departmentsOptions: IntegrationsDatevLaudsDepartment[]
  healthInsurersOptions: IntegrationsDatevLaudsHealthInsurer[]
  clientId: string
}

export const modifiedClientData = (
  clientData:
    | IntegrationsDatevLaudsBusinessUnit[]
    | IntegrationsDatevLaudsSalaryType[]
    | IntegrationsDatevLaudsDepartment[]
): Item[] =>
  clientData.map(({ id, name }) => ({
    value: id,
    label: name,
  }))

export const getMatchedLegalEntities = (data: DatevWizardData, company: Company) => {
  const legalEntitiesKeys = Object.keys(data.answers).filter(
    (key) => /^legal_entity_\d+$/.test(key) && !!data.answers[key]
  )
  const mappedLegalEntityIds = legalEntitiesKeys.map((key) => key.split('_')[2])
  return company.legalEntities
    .filter((legalEntity) => mappedLegalEntityIds.includes(legalEntity.id.toString()))
    .map((legalEntity) => legalEntity)
}

const createMapping = (id: string, code: string, legalEntityId: string): DatevMapping => {
  return {
    factorial_id: id,
    datev_id: code,
    meta: {
      legal_entity_id: legalEntityId,
    },
  }
}

const upsertMapping = (
  mappings: DatevMapping[],
  dropdownItemValue: string,
  factorialId: string,
  legalEntityId: string
): DatevMapping[] => {
  // Unset mappings
  if (!dropdownItemValue) {
    return mappings.filter(
      (mapping) =>
        mapping?.meta?.legal_entity_id !== legalEntityId || mapping.factorial_id !== factorialId
    )
  }

  const code = dropdownItemValue
  let isMapped = false

  // Update existing mappings
  const newMappings = mappings.map((mapping) => {
    if (mapping?.meta?.legal_entity_id === legalEntityId && mapping.factorial_id === factorialId) {
      isMapped = true
      return { ...mapping, datev_id: code }
    }
    return mapping
  })

  // Add new mapping if not found
  if (!isMapped) {
    newMappings.push(createMapping(factorialId, code, legalEntityId))
  }

  return newMappings
}

// Specific functions using the generic implementation
export const upsertCompensationMappings = (
  mappings: DatevMapping[],
  dropdownItemValue: string,
  factorialId: string,
  legalEntityId: string
) => upsertMapping(mappings, dropdownItemValue, factorialId, legalEntityId)

export const upsertDepartmentMappings = (
  mappings: DatevMapping[],
  dropdownItemValue: string,
  factorialId: string,
  legalEntityId: string
) => upsertMapping(mappings, dropdownItemValue, factorialId, legalEntityId)

export const absenceCodes = (data: DatevWizardData): Item[] => {
  const payrollSoftware = data.answers.payroll_software

  switch (payrollSoftware) {
    case 'datev_lodas':
      return [
        {
          value: 32,
          label: '32 -Aussperrung',
        },
        {
          value: 47,
          label: '47 - Krank bei Eintrit o. Lfz.',
        },
        {
          value: 300,
          label: '300 - Entgeltfortzahlung wegen Organ-/Gewebespende',
        },
        {
          value: 29,
          label: '29 - Einstellung Krankengeld w. voller Erwerbsminderungs-/ Altersrente',
        },
        {
          value: 64,
          label: '64 - Eignungsübung',
        },
        {
          value: 51,
          label: '51 - Elternzeit',
        },
        {
          value: 301,
          label: '301 - Freistellung im Rahmen sonstiger flexibler Arbeitszeitregelungen',
        },
        {
          value: 61,
          label: '61 - Wehrdienst',
        },
        {
          value: 33,
          label: '33 - Entgeltfortzahlung ohne AU ',
        },
        {
          value: 103,
          label: '103 - Entgeltfortzahlung mit AU',
        },
        {
          value: 41,
          label: '41 - Kranken(tage)geld bei Krankheit oder Kur',
        },
        {
          value: 260,
          label: '260 - Ende Bezug Krankengeld / Beginn Bezug Arbeitslosengeld',
        },
        {
          value: 28,
          label: '28 - Aussteuerung',
        },
        {
          value: 25,
          label: '25 - Krank privat Versicherte ohne Krankentagegeld',
        },
        {
          value: 16,
          label: '16 - Kurzzeitige Pflege',
        },
        {
          value: 45,
          label: '45 - Mutterschutzfrist',
        },
        {
          value: 302,
          label: '302 - Krankengeld bei Organ-/Blut-/Gewebespende ohne EEL',
        },
        {
          value: 14,
          label: '14 - Vollständige Freistellung für Betreuung/Begleitung Pflegebedürftiger',
        },
        {
          value: 200,
          label: '200 - Kinderpflege mit Entgeltfortzahlung',
        },
        {
          value: 11,
          label: '11 - Kinderpflege mit Krankengeld',
        },
        {
          value: 24,
          label: '24 - Kinderpflege ohne Krankengeld',
        },
        {
          value: 210,
          label: '210 - Pflegeunterstützungsgeld',
        },
        {
          value: 15,
          label: '15 - Pflegezeit mit vollständiger Freistellung',
        },
        {
          value: 303,
          label: '303 - Entschädigungszahlung wegen Quarantäne',
        },
        {
          value: 305,
          label: '305 - Bezahlte Freistellung wegen freiwilliger Quarantäne',
        },
        {
          value: 304,
          label: '304 - Bezahlte Freistellung wegen angeordneter Quarantäne',
        },
        {
          value: 306,
          label: '306 - Unbezahlte Freistellung wegen freiwilliger Quarantäne',
        },
        {
          value: 31,
          label: '31 - Rechtmäßiger Streik',
        },
        {
          value: 106,
          label: '106 - Studienzeit (dualer Studiengang)',
        },
        {
          value: 23,
          label: '23 - Unrechtmäßiger Streik',
        },
        {
          value: 105,
          label: '105 - Unwiderrufliche bezahlte Freistellung',
        },
        {
          value: 43,
          label: '43 - Übergangsgeld bei Krankheit oder Kur',
        },
        {
          value: 19,
          label: '19 - Unwiderrufliche unbezahlte Freistellung',
        },
        {
          value: 21,
          label: '21 - Unbezahlter Urlaub',
        },
        {
          value: 44,
          label: '44 - Versogungsgeld bei Krankheit oder Kur',
        },
        {
          value: 42,
          label: '42 - Verletztengeld bei Krankheit oder Kur',
        },
        {
          value: 63,
          label: '63 - Wehrübung',
        },
        {
          value: 81,
          label: '81 - KUG / S-KUG Krank',
        },
        {
          value: 62,
          label: '62 - Zivildienst',
        },
      ]
    case 'datev_lug':
      return [
        {
          value: 1,
          label: '1 - Zeitlohn',
        },
        {
          value: 2,
          label: '2 - Zeitlohn 2',
        },
        {
          value: 3,
          label: '3 - Zeitlohn 3',
        },
        {
          value: 4,
          label: '4 - Zeitlohn 4',
        },
        {
          value: 5,
          label: '5 - Zeitlohn 5',
        },
        {
          value: 'AA',
          label: 'AA - Arbeitszeitkonto Aufbau',
        },
        {
          value: 'AE',
          label: 'AE - Arbeitszeitkonto Entnahme',
        },
        {
          value: 'AF',
          label: 'AF - Arbeitsfrei ohne Lohnanspruch',
        },
        {
          value: 'AM',
          label: 'AM - Ausbildung zum Meister',
        },
        {
          value: 'AS',
          label: 'AS - Aussperrung',
        },
        {
          value: 'EK',
          label: 'EK - Krank ab Eintritt o. Lfz.',
        },
        {
          value: 'BR',
          label: 'BR - Betriebsratstätigkeit',
        },
        {
          value: 'EO',
          label: 'EO - Entgeltfortzahlung wegen Organ-/Gewebespende',
        },
        {
          value: 'ER',
          label: 'ER - Einstellung Krankengeld w. voller Erwerbsminderungs-/ Altersrente',
        },
        {
          value: 'ES',
          label: 'ES - Entgelt in Höhe Kug (SV durch AN und AG)',
        },
        {
          value: 'EÜ',
          label: 'EÜ - Eignungsübung',
        },
        {
          value: 'EZ',
          label: 'EZ - Elternzeit',
        },
        {
          value: 'F',
          label: 'F - Feiertag',
        },
        {
          value: 'FE',
          label: 'FE - Fehlt entschuldigt (bezahlt)',
        },
        {
          value: 'FK',
          label: 'FK - Feiertag im Kug-Zeitraum (SV durch AG)',
        },
        {
          value: 'FN',
          label: 'FN - Fehlstunden unbez./unentsch. (k. vollen Tage)',
        },
        {
          value: 'FR',
          label: 'FR - Freistellung im Rahmen sonst. flexibler Arbeitszeitregelungen',
        },
        {
          value: 'FS',
          label: 'FS - Fehlstunden unbezahlt (keine vollen Tage)',
        },
        {
          value: 'FU',
          label: 'FU - Fehltag unentschuldigt',
        },
        {
          value: 'GR',
          label: 'GR - Grund- bzw. freiwilliger Wehrdienst',
        },
        {
          value: 'K',
          label: 'K - Krankheit (Lohnfortzahlung)',
        },
        {
          value: 'K6',
          label: 'K6 - Krankheit mit Kranken(tage)geldanspruch',
        },
        {
          value: 'KA',
          label: 'KA - Ende Bezug Krankengeld / Beginn Bezug Arbeitslosengeld',
        },
        {
          value: 'KB',
          label: 'KB - Krankheit Betriebsunfall (LFZ)',
        },
        {
          value: 'KE',
          label: 'KE - Krankengeldende nach (78 Wochen)',
        },
        {
          value: 'KF',
          label: 'KF - Krank am Feiertag',
        },
        {
          value: 'KG',
          label: 'KG - Krank am Feiertag in Höhe Kug (SV durch AG)',
        },
        {
          value: 'KK',
          label: 'KK - Krank vor Saison-Kug',
        },
        {
          value: 'KO',
          label: 'KO - Krankheit ohne Kranken(tage)geldanspruch',
        },
        {
          value: 'KP',
          label: 'KP - Kurzzeitige Pflege',
        },
        {
          value: 'KS',
          label: 'KS - Krankengeld bei witterungsbed. Ausf. (Gerüstbau-Stunden)',
        },
        {
          value: 'KT',
          label: 'KT - Krankengeld bei witterungsbed. Ausf. (Gerüstbau-Tage)',
        },
        {
          value: 'KU',
          label: 'KU - Kurzarbeit (Kug)',
        },
        {
          value: 'KV',
          label: 'KV - Krank während Überbrückungsgeld bei witterungsbed. Ausfall (Gerüstbau)',
        },
        {
          value: 'KW',
          label: 'KW - Krank nach witterungsbedingtem Ausfall',
        },
        {
          value: 'LA',
          label: 'LA - Lohnausgleich',
        },
        {
          value: 'ME',
          label: 'ME - Mehrarbeit',
        },
        {
          value: 'MK',
          label: 'MK - Krankengeld bei Mitaufnahme ins Krankenhaus',
        },
        {
          value: 'ML',
          label: 'ML - Mutterschutzlohn/Beschäftigungsverbot',
        },
        {
          value: 'MS',
          label: 'MS - Mutterschutzfrist',
        },
        {
          value: 'OS',
          label: 'OS - Krankengeld bei Organ-/Blut-/Gewebespende ohne EEL',
        },
        {
          value: 'PB',
          label: 'PB - Vollständige Freistellung für Betreuung/Begleitung Pflegebedürftiger',
        },
        {
          value: 'PE',
          label: 'PE - Pflege krankes Kind mit Entgeltfortzahlung',
        },
        {
          value: 'PK',
          label: 'PK - Pflege krankes Kind mit Kranken(tage)geld',
        },
        {
          value: 'PO',
          label: 'PO - Pflege krankes Kind ohne Kranken(tage)geld',
        },
        {
          value: 'PS',
          label: 'PS - Pflegestunden krankes Kind unbez. (k. vollen Tage)',
        },
        {
          value: 'PU',
          label: 'PU - Pflegeunterstützungsgeld',
        },
        {
          value: 'PZ',
          label: 'PZ - Pflegezeit',
        },
        {
          value: 'QA',
          label: 'QA - Quarantäne mit Entschädigungszahlung',
        },
        {
          value: 'QB',
          label: 'QB - Beaufsichtigung Kind (Stunden) wegen Schulschließung (§56. Abs. 1a IfSG)',
        },
        {
          value: 'QC',
          label: 'QC - Freiwillige Einstellung der beruflichen Tätigkeit (bezahlte Freistellung)',
        },
        {
          value: 'QD',
          label: 'QD - Freiwillige Einstellung der beruflichen Tätigkeit (unbezahlte Freistellung)',
        },
        {
          value: 'QF',
          label: 'QF - Freiwillige Quarantäne (bezahlte Freistellung)',
        },
        {
          value: 'QG',
          label: 'QG - Freiwillige Quarantäne Ausfallstunden (bezahlte Freistellung)',
        },
        {
          value: 'QK',
          label: 'QK - Beaufsichtigung Kind wegen Schulschließung (§ 56. Abs. 1a IfSG)',
        },
        {
          value: 'QN',
          label: 'QN - Freiwillige Quarantäne Ausfallstunden (unbezahlte Freistellung)',
        },
        {
          value: 'QO',
          label: 'QO - Angeordnete Quarantänge (bezahlte Freistellung)',
        },
        {
          value: 'QS',
          label: 'QS - Quarantäne Ausfallstunden mit Entschädigungszahlung',
        },
        {
          value: 'QT',
          label: 'QT - berufliches Tätigkeitsverbot mit Entschädigungszahlung',
        },
        {
          value: 'QU',
          label: 'QU - Freiwillige Quarantäne (unbezahlte Freistellung)',
        },
        {
          value: 'RA',
          label: 'RA - Rechtmäßiger Streik (Tage)',
        },
        {
          value: 'RH',
          label: 'RH - Reha/Kur mit Entgeltfortzahlung',
        },
        {
          value: 'SO',
          label: 'SO - Sonstige Stunden/Zulagen',
        },
        {
          value: 'ST',
          label: 'ST - Sozialstunden',
        },
        {
          value: 'SU',
          label: 'SU - Sonderurlaub',
        },
        {
          value: 'SZ',
          label: 'SZ - Studienzeit (dualer Studiengang)',
        },
        {
          value: 'U',
          label: 'U - Urlaub',
        },
        {
          value: 'U6',
          label: 'U6 - Krank Betriebsunfall (ohne LFZ)',
        },
        {
          value: 'UA',
          label: 'UA - Unrechtm. Arbeitskampf',
        },
        {
          value: 'UB',
          label: 'UB - Unwiderrufliche bezahlte Freistellung',
        },
        {
          value: 'UE',
          label: 'UE - Übergangsgeld',
        },
        {
          value: 'UF',
          label: 'UF - Unwiderrufliche unbezahlte Freistellung',
        },
        {
          value: 'UU',
          label: 'UU - Unbezahlter Urlaub',
        },
        {
          value: 'VG',
          label: 'VG - Versorgungsgeld',
        },
        {
          value: 'VK',
          label: 'VK - Krankengeld/Krankheit vor Kug (Tage)',
        },
        {
          value: 'VL',
          label: 'VL - Verletztengeld',
        },
        {
          value: 'VS',
          label: 'VS - Krankstunden vor Kug',
        },
        {
          value: 'W1',
          label: 'W1 - Witt.bed. Ausfall unter 1 Std./Tag',
        },
        {
          value: 'WA',
          label: 'WA - Winterausfall',
        },
        {
          value: 'WD',
          label: 'WD - Witterungsbedingter Ausfall Dachdecker',
        },
        {
          value: 'WE',
          label: 'WE - Wehrübung',
        },
        {
          value: 'WK',
          label: 'WK - Krank während Kug',
        },
        {
          value: 'ZD',
          label: 'ZD - Zivil- bzw. Bundesfreiwilligendienst',
        },
      ]
    default:
      throw new Error(`Unknown payroll software ${payrollSoftware}`)
  }
}
